import { DocumentQueryParams, ExtraParams, TrashedFilesQueryParams } from "../types/document-manager.type";
import { ConfigurationSettings } from "/models/configuration-settings";
import { clients } from "lib/https";
import { columnIdToSortKey, extensionToFileType, getExtensionsForFileType, IFileDataWithSharing, IFileTrashData } from "../utils/file-manager";
import { formatFileSize } from "../utils/file-manager";
import { CurrentUser } from "/state/store/common/useCommonStore";

export enum ShareholderType {  
    USER = "USER",
    ORGANIZATION = "ORGANIZATION",
    USER_GROUP = "USER_GROUP",
    TENANT = "TENANT",
}

export enum VectorizationStatus {
    PENDING = "PENDING",
    IN_PROGRESS = "IN_PROGRESS",
    SUCCESS = "SUCCESS",
    FAILED = "FAILED"
}

export interface Shareholder {
    id: string;
    type: ShareholderType;
}

export interface FileShareMetadata {
    shareholderId: Shareholder;
    sharedAt: string;
}

export interface UploadedFileResponse {
    fileId: string;
    ownerId: string;
    createdDate: string;
    lastUpdatedDate: string;
    metadata: {
      fileName: string;
      extension: string;
      fileSize: number;
      folderName: string | null;
      tags: string[];
    };
    folderId: string | null;
    softDeleted: boolean;
    vectorizationStatus: VectorizationStatus;
    fileShareMetadata: FileShareMetadata[];
    softDeletedAt: string;
}

export interface DocumentResponse {
    data: UploadedFileResponse[];
    totalCount: number;
}

interface TransformedResponse {
    data: IFileDataWithSharing[];
    totalCount: number;
}

export interface TrashTransformedResponse {
    data: IFileTrashData[];
    totalCount: number;
}
  

export const uploadFileUnderDocument = (configurationSettings: ConfigurationSettings, file: File): Promise<UploadedFileResponse> => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFilesUpload}`;

    const formData = new FormData();
    formData.append('file', file);
    
    return clients.post<UploadedFileResponse>(url, configurationSettings.blocksKey, formData, {}, true);
}

export const getDocumentFiles = async (configurationSettings: ConfigurationSettings, queryParams: DocumentQueryParams&ExtraParams, user: CurrentUser | null): Promise<TransformedResponse> => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;

    const params = new URLSearchParams({
        page: (queryParams.page + 1).toString(),
        pageSize: queryParams.pageSize.toString(),
        ...(queryParams.sharedOnly !== undefined && { sharedOnly: queryParams.sharedOnly.toString() }),
        ...(queryParams.softDeleted !== undefined && { softDeleted: queryParams.softDeleted.toString() }),
    })
    if (queryParams.filter.name) {
        params.set('fileName', queryParams.filter.name);
    }
    if (queryParams.filter.fileType) {
        params.set('extension', getExtensionsForFileType(queryParams.filter.fileType));
    }
    if (queryParams.filter.lastModified?.from) {
        params.set('startDate', queryParams.filter.lastModified.from.getTime().toString());
    }
    if (queryParams.filter.lastModified?.to) {
        params.set('endDate', queryParams.filter.lastModified.to.getTime().toString());
    }
    if (queryParams.filter.sharedDate?.from) {
        params.set('sharedStartDate', queryParams.filter.sharedDate.from.getTime().toString());
    }
    if (queryParams.filter.sharedDate?.to) {
        params.set('sharedEndDate', queryParams.filter.sharedDate.to.getTime().toString());
    }
    if (queryParams.sort && queryParams.sort.length > 0) {
        params.set('sortBy', columnIdToSortKey(queryParams.sort[0].id));
        params.set('order', queryParams.sort[0].desc ? 'desc' : 'asc');
    }

    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFiles}?${params.toString()}`;
    const response = await clients.get<DocumentResponse>(url, configurationSettings.blocksKey);
    const transformedResponse: TransformedResponse = {
        data: response.data.map((file) => ({
            id: file.fileId,
            name: file.metadata.fileName,
            ownerId: file.ownerId,
            lastModified: new Date(file.lastUpdatedDate),
            fileType: extensionToFileType(file.metadata.extension),
            size: formatFileSize(file.metadata.fileSize),
            isShared: file.fileShareMetadata.length > 0,
            isSharedWithTenant: file.fileShareMetadata.some((shareholder) => shareholder.shareholderId.type === 'TENANT'),
            sharedWith: file.fileShareMetadata.map((shareholder) => ({
                id: shareholder.shareholderId.id,
                type: shareholder.shareholderId.type,
            })),
            sharedDate: file.fileShareMetadata.length > 0
            ? (() => {
                const match = file.fileShareMetadata.find(
                    (shareholder) => shareholder.shareholderId.id === user?.id || shareholder.shareholderId.type === 'TENANT'
                );
                return match ? new Date(match.sharedAt) : undefined;
            })()
            : undefined,
            vectorizationStatus: file.vectorizationStatus,
            metadata: file.metadata,
        })),
        totalCount: response.totalCount,
    };
    return transformedResponse;
}

export const getDocumentTrashFiles = async (configurationSettings: ConfigurationSettings, queryParams: TrashedFilesQueryParams): Promise<TrashTransformedResponse> => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;

    const params = new URLSearchParams({
        page: (queryParams.page + 1).toString(),
        pageSize: queryParams.pageSize.toString(),
        ...(queryParams.sharedOnly !== undefined && { sharedOnly: queryParams.sharedOnly.toString() }),
        ...(queryParams.softDeleted !== undefined && { softDeleted: queryParams.softDeleted.toString() }),
    })
    if (queryParams.filter.name) {
        params.set('fileName', queryParams.filter.name);
    }
    if (queryParams.filter.fileType) {
        params.set('extension', getExtensionsForFileType(queryParams.filter.fileType));
    }
    if (queryParams.filter.lastModified?.from) {
        params.set('startDate', queryParams.filter.lastModified.from.getTime().toString());
    }
    if (queryParams.filter.lastModified?.to) {
        params.set('endDate', queryParams.filter.lastModified.to.getTime().toString());
    }
    if(queryParams.filter.trashedDate?.from){
        params.set('softDeletedStartDate', queryParams.filter.trashedDate.from.getTime().toString());
    }
    if(queryParams.filter.trashedDate?.to){
        params.set('softDeletedEndDate', queryParams.filter.trashedDate.to.getTime().toString());
    }

    params.set('sortBy', queryParams.sort && queryParams.sort.length > 0 ? columnIdToSortKey(queryParams.sort[0].id) : 'softDeletedAt');
    params.set('order', queryParams.sort && queryParams.sort.length > 0 ? (queryParams.sort[0].desc ? 'desc' : 'asc') : 'desc');

    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFiles}?${params.toString()}`;
    const response = await clients.get<DocumentResponse>(url, configurationSettings.blocksKey);
    const transformedResponse: TrashTransformedResponse = {
        data: response.data.map((file) => ({
            id: file.fileId,
            name: file.metadata.fileName,
            lastModified: new Date(file.lastUpdatedDate),
            fileType: extensionToFileType(file.metadata.extension),
            size: formatFileSize(file.metadata.fileSize),
            trashedDate: new Date(file.lastUpdatedDate),
            isShared: file.fileShareMetadata.length > 0
        })),
        totalCount: response.totalCount,
    };
    return transformedResponse;
}


export const renameDocumentFile =(configurationSettings: ConfigurationSettings, fileId: string, newName: string) => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFiles}`;
    const body = {
        "fileId": fileId,
        "fileName": newName
    }
    return clients.put(url, configurationSettings.blocksKey, JSON.stringify(body));
}

export const updateFileSoftDeletedStatus = (
    configurationSettings: ConfigurationSettings,
    fileId: string,
    softDeleted: boolean
  ) => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFiles}`;
  
    const body = {
      fileId: fileId,
      softDelete: softDeleted,
    };
  
    return clients.put(url, configurationSettings.blocksKey, JSON.stringify(body));
  };
  
    
export const shareDocumentFile = (configurationSettings: ConfigurationSettings, fileId: string, users: {id: string, type: ShareholderType}[]) => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFilesShare}`;
    const body = {
        "fileId": fileId,
        "userIds": users
    }
    return clients.post(url, configurationSettings.blocksKey, JSON.stringify(body));
}

export const unshareDocumentFile = (configurationSettings: ConfigurationSettings, fileId: string, users: {id: string, type: ShareholderType}[]) => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFilesUnshare}`;
    const body = {
        "fileId": fileId,
        "userIds": users
    }
    return clients.post(url, configurationSettings.blocksKey, JSON.stringify(body));
}

export const softDeleteDocumentFile = (configurationSettings: ConfigurationSettings, fileIds: string[]) => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFilesDelete}`;
    const body = {
        "fileIds": fileIds,
        "softDelete": true
    }
    return clients.post(url, configurationSettings.blocksKey, JSON.stringify(body));
}

export const hardDeleteDocumentFile = (configurationSettings: ConfigurationSettings, fileIds: string[]) => {
    const baseUrl = `${configurationSettings.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${configurationSettings?.paths?.seliseAi.documentManagerFilesDelete}`;
    const body = {
        "fileIds": fileIds,
        "softDelete": false
    }
    return clients.post(url, configurationSettings.blocksKey, JSON.stringify(body));
}